/**
 * VIP 订单详情独立页 — frame a5cbV0 @ ui/dogdisk-user.pen 页面-VIP订单详情。
 *
 * 路径:`/vip/orders/:id`(per spec §4.4)
 * 守卫:🔒 Authed(owner;AppLayout 自动包 RequireAuth 等价行为)
 *
 * 复用既有 endpoint:GET /gapi/user/vip/orders/{id}(扩展字段集 per vip-purchase.md §3)
 * 与 routes/orders.tsx 区分:orders.tsx 是列表 + drawer;本 frame 是独立页。
 *
 * Spec:docs/superpowers/specs/2026-05-26-batch-d-user-routes-design.md §4.4
 */
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Spinner, apiClient, cn } from '@dogdisk/shared'
import { AppLayout } from '../../../components/app-layout'
import { MobileScreen, TopBar } from '../../../components/mobile-screen'

const F = 'a5cbV0'

// 对齐 orders.tsx 的 VipOrderStatus 字段(2026-05-25 P3-W9 B4-#3 扩展)
type VipOrderStatus = {
  order_id: string
  plan_id: string
  plan_name: string
  status: string
  amount_cny_cents: number
  paid_at: string | null
  next_billing_at: string | null
  price_cny_cents: number
  purchased_at: string | null
  expires_at: string | null
  days_remaining: number
  auto_renew: boolean
  payment_provider: string
  transaction_id: string
  vip_tier_at_purchase: string
}

const PROVIDER_LABEL: Record<string, string> = {
  wechat_pay: '微信支付',
  alipay: '支付宝',
  coin: '金币',
  '': '—',
}

const STATUS_LABEL: Record<string, string> = {
  paid: '已完成',
  pending: '待支付',
  cancelled: '已取消',
  failed: '失败',
  expired: '已过期',
}

const STATUS_CLASS: Record<string, string> = {
  paid: 'bg-[#FFE4E4] text-[#FF6B6B]',
  pending: 'bg-[#FEF3C7] text-[#F59E0B]',
  cancelled: 'bg-[#F1F2F4] text-[#9CA3AF]',
  failed: 'bg-[#FEE2E2] text-[#DC2626]',
  expired: 'bg-[#F1F2F4] text-[#9CA3AF]',
}

const TIER_LABEL: Record<string, string> = {
  bronze: '青铜',
  silver: '白银',
  gold: '黄金',
  platinum: '铂金',
}

export function VipOrderDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['vip-order-status', id] as const,
    enabled: Boolean(id),
    queryFn: () => apiClient.get<VipOrderStatus>(`/gapi/user/vip/orders/${id!}`),
  })

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="订单详情" />

        <main data-frame-id={F} data-testid={F} className="flex flex-1 flex-col gap-4 px-6 pt-3 pb-12">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner className="text-gray-400" />
            </div>
          ) : error ? (
            <p className="px-6 py-4 text-center text-[12px] text-[#FF6B6B]">
              {error instanceof Error ? error.message : '查询失败'}
            </p>
          ) : data ? (
            <>
              {/* vdStat 卡:状态 + 套餐 + 金额 */}
              <section className="rounded-2xl bg-gradient-to-br from-[#FFE4E4] to-[#FEF3C7] p-5">
                <div className="flex items-center gap-2">
                  <span className="text-[24px]">👑</span>
                  <span
                    data-testid={`${F}-status`}
                    className={cn(
                      'rounded-md px-2 py-0.5 text-[11px] font-medium',
                      STATUS_CLASS[data.status] ?? 'bg-[#F1F2F4] text-[#9CA3AF]',
                    )}
                  >
                    {STATUS_LABEL[data.status] ?? data.status}
                  </span>
                </div>
                <p
                  data-testid={`${F}-plan`}
                  className="mt-3 text-[18px] font-semibold text-[#1A1A1A]"
                >
                  {data.plan_name || data.plan_id}
                  {data.vip_tier_at_purchase && TIER_LABEL[data.vip_tier_at_purchase] && (
                    <span className="ml-1 text-[12px] text-[#9CA3AF]">
                      · {TIER_LABEL[data.vip_tier_at_purchase]}
                    </span>
                  )}
                </p>
                <p
                  data-testid={`${F}-amount`}
                  className="mt-1 text-[24px] font-bold tabular-nums text-[#FF6B6B]"
                >
                  ¥{(data.amount_cny_cents / 100).toFixed(2)}
                </p>
              </section>

              {/* vdInfo 5 行:订单号 / 套餐 / 支付方式 / 购买时间 / VIP 有效期 */}
              <section className="rounded-2xl bg-white">
                <dl className="grid grid-cols-2 gap-y-3 px-5 py-4 text-[13px]">
                  <dt className="text-[#9CA3AF]">订单号</dt>
                  <dd className="text-right font-mono text-[11px] text-[#9CA3AF]">
                    {data.order_id.slice(0, 12)}…
                  </dd>

                  <dt className="text-[#9CA3AF]">支付方式</dt>
                  <dd className="text-right text-[#1A1A1A]">
                    {PROVIDER_LABEL[data.payment_provider] ?? data.payment_provider}
                  </dd>

                  <dt className="text-[#9CA3AF]">购买时间</dt>
                  <dd className="text-right text-[#1A1A1A]">
                    {data.purchased_at
                      ? data.purchased_at.slice(0, 16).replace('T', ' ')
                      : data.paid_at
                        ? data.paid_at.slice(0, 16).replace('T', ' ')
                        : '—'}
                  </dd>

                  <dt className="text-[#9CA3AF]">VIP 有效期</dt>
                  <dd className="text-right text-[#1A1A1A]">
                    {data.expires_at
                      ? `至 ${data.expires_at.slice(0, 10)}`
                      : data.next_billing_at
                        ? `至 ${data.next_billing_at.slice(0, 10)}`
                        : '—'}
                  </dd>

                  <dt className="text-[#9CA3AF]">剩余天数</dt>
                  <dd className="text-right tabular-nums text-[#1A1A1A]">
                    {data.status === 'paid' && data.days_remaining > 0
                      ? `${data.days_remaining} 天`
                      : '—'}
                  </dd>

                  <dt className="text-[#9CA3AF]">流水号</dt>
                  <dd className="text-right font-mono text-[11px] text-[#9CA3AF] break-all">
                    {data.transaction_id || '—'}
                  </dd>
                </dl>
              </section>

              {/* CTA:续费 + 申请发票(发票 modal 走 P3 backlog) */}
              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="button"
                  data-testid={`${F}-renew-btn`}
                  onClick={() => navigate('/vip/buy')}
                  className="h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b]"
                >
                  续费 VIP
                </button>
                <button
                  type="button"
                  data-testid={`${F}-invoice-btn`}
                  disabled
                  className="h-12 rounded-lg border border-[#E5E7EB] bg-white text-[15px] font-semibold text-[#9CA3AF]"
                >
                  申请发票(敬请期待)
                </button>
              </div>
            </>
          ) : (
            <p className="mt-12 text-center text-[13px] text-[#9CA3AF]">订单不存在</p>
          )}
        </main>
      </MobileScreen>
    </AppLayout>
  )
}

export default VipOrderDetailPage
